// src/components/gestionEvento/exportTypes/exportCustomLogic.js

import Papa from 'papaparse';
import { formatMarkForComplete } from '../../../utils/meetUtils';

export function exportCustomLogic({
  inscripciones,
  selectedFields = [],
  competitionName = 'Competencia',
  delimiter = ',',
  forceQuoted = false,
}) {
  if (!Array.isArray(inscripciones)) {
    console.warn('inscripciones no es un array. Se usará array vacío.');
    inscripciones = [];
  }

  // ¿El usuario marcó "Pruebas"?
  const includePruebas = selectedFields.includes('Pruebas');
  // ¿El usuario marcó "Relevos"?
  const includeRelevos = selectedFields.includes('Relevos');

  // Filtramos los campos base (excluyendo "Pruebas" y "Relevos")
  const baseFields = selectedFields.filter(
    (f) => f !== 'Pruebas' && f !== 'Relevos'
  );

  if (selectedFields.length === 0) {
    console.warn('Ninguna columna seleccionada. Se cancela la exportación.');
    return;
  }

  // Calculamos máximos solo si se eligió Pruebas o Relevos
  let maxTests = 0, maxRelays = 0, maxIntegrantes = 0, maxSuplentes = 0;
  if (includePruebas || includeRelevos) {
    maxTests = inscripciones.reduce((max, insc) => {
      const count = insc.pruebasData ? insc.pruebasData.length : 0;
      return Math.max(max, count);
    }, 0);

    maxRelays = inscripciones.reduce((max, insc) => {
      const count = insc.relevosData ? insc.relevosData.length : 0;
      return Math.max(max, count);
    }, 0);

    maxIntegrantes = inscripciones.reduce((max, insc) => {
      if (insc.relevosData) {
        const localMax = Math.max(
          ...insc.relevosData.map((r) => (r.integrantes ? r.integrantes.length : 0))
        );
        return Math.max(max, localMax);
      }
      return max;
    }, 0);

    maxSuplentes = inscripciones.reduce((max, insc) => {
      if (insc.relevosData) {
        const localMax = Math.max(
          ...insc.relevosData.map((r) => (r.suplentes ? r.suplentes.length : 0))
        );
        return Math.max(max, localMax);
      }
      return max;
    }, 0);
  }

  // 1) Construimos el encabezado: primero los baseFields
  const header = [...baseFields];

  // Si incluye Pruebas
  if (includePruebas) {
    for (let i = 1; i <= maxTests; i++) {
      header.push(`Prueba ${i}`);
      header.push(`Marca ${i}`);
    }
  }

  // Si incluye Relevos
  if (includeRelevos) {
    for (let j = 1; j <= maxRelays; j++) {
      header.push(`Relevo ${j} Prueba`);
      header.push(`Relevo ${j} EquipoTipo`);
      header.push(`Relevo ${j} MarcaSiembra`);
      for (let k = 1; k <= maxIntegrantes; k++) {
        header.push(`Relevo ${j} Integrante ${k}`);
      }
      for (let k = 1; k <= maxSuplentes; k++) {
        header.push(`Relevo ${j} Suplente ${k}`);
      }
    }
  }

  // 2) Armamos csvData con la primera fila = header
  const csvData = [];
  csvData.push(header);

  // 3) Para cada inscrito, construimos su fila
  inscripciones.forEach((insc) => {
    const row = [];

    // 3.1) Campos base
    baseFields.forEach((field) => {
      let val = '';

      switch (field) {
        case 'CompetitionID':       val = ''; break; // o competitionId global
        case 'CompetitionName':     val = competitionName; break;
        case 'CodigoInscripcion':   val = insc.codigoId || ''; break;
        case 'Nombre':              val = insc.nombre || ''; break;
        case 'Apellido':            val = insc.apellido || ''; break;
        case 'Email':               val = insc.email || ''; break;
        case 'Teléfono':            val = insc.telefono || ''; break;
        case 'Rama':                val = insc.rama || ''; break;
        case 'Categoría':           val = insc.categoria || ''; break;
        case 'CatNumber':           val = insc.catNumber ?? ''; break;
        case 'Dirección':           val = (insc.direccion || '').replace(/,/g, ' '); break;
        case 'Ciudad':              val = (insc.ciudad || '').replace(/,/g, ' '); break;
        case 'Estado':              val = (insc.estado || '').replace(/,/g, ' '); break;
        case 'País':                val = (insc.pais || '').replace(/,/g, ' '); break;
        case 'FechaRegistro':       val = insc.fechaRegistro || ''; break;
        case 'PaymentMethod':       val = insc.paymentMethod || ''; break;
        case 'Asociacion':          val = insc.asociacion || ''; break;
        case 'DiaNacimiento':       val = insc.diaNacimiento ?? ''; break;
        case 'MesNacimiento':       val = insc.mesNacimiento ?? ''; break;
        case 'AnioNacimiento':      val = insc.anioNacimiento ?? ''; break;
        case 'FotoPerfil':          val = ''; break; // forzamos vacío
        case 'LogoEquipo':          val = ''; break; // forzamos vacío
        case 'EquipoNombre':
          if (insc.equipo && typeof insc.equipo === 'object') {
            val = insc.equipo.name || '';
          } else if (typeof insc.equipo === 'string') {
            val = insc.equipo;
          }
          break;
        case 'EquipoTeamCode':
          if (insc.equipo && typeof insc.equipo === 'object') {
            val = insc.equipo.teamCode || '';
          }
          break;
        case 'ContactoNombre':
          if (Array.isArray(insc.contactoEmergencia) && insc.contactoEmergencia[0]) {
            val = insc.contactoEmergencia[0].nombreEmergencia || '';
          }
          break;
        case 'ContactoTeléfono':
          if (Array.isArray(insc.contactoEmergencia) && insc.contactoEmergencia[0]) {
            val = insc.contactoEmergencia[0].telefonoEmergencia || '';
          }
          break;
        case 'TutorNombre':         val = insc.tutorNombre || ''; break;
        case 'TutorEmail':          val = insc.tutorEmail || ''; break;
        case 'TutorTeléfono':       val = insc.tutorTelefono || ''; break;
        default:
          val = '';
          break;
      }

      // NO forzamos comillas aquí, solo devolvemos val tal cual
      row.push(val);
    });

    // 3.2) Pruebas
    if (includePruebas) {
      // Para cada prueba, generamos (Prueba i, Marca i)
      const testsCount = insc.pruebasData?.length || 0;
      for (let i = 0; i < maxTests; i++) {
        if (i < testsCount) {
          const prueba = insc.pruebasData[i];
          // Nombre de la prueba
          row.push(prueba.prueba || '');
          // Marca -> forzar comillas
          let rawMark = formatMarkForComplete(prueba);
          const tipo = (prueba.tipoMarca || '').toLowerCase();
          // Si forceQuoted y es fondo/mediofondo => `'06:25.99`
          if (forceQuoted && (tipo === 'fondo' || tipo === 'mediofondo')) {
            rawMark = `'${rawMark}`;
          }
          row.push(rawMark);
        } else {
          // No hay prueba en este index => vacíos
          row.push('');
          row.push('');
        }
      }
    }

    // 3.3) Relevos
    if (includeRelevos) {
      const relaysCount = insc.relevosData?.length || 0;
      for (let j = 0; j < maxRelays; j++) {
        if (j < relaysCount) {
          const relevo = insc.relevosData[j];
          // Relevo j Prueba
          row.push(relevo.prueba || '');
          // Relevo j EquipoTipo
          row.push(relevo.equipoTipo || '');
          // Relevo j MarcaSiembra
          let raw = formatMarkForComplete(relevo);
          const tipo = (relevo.tipoMarca || '').toLowerCase();
          if (forceQuoted && (tipo === 'fondo' || tipo === 'mediofondo')) {
            raw = `'${raw}`;
          }
          row.push(raw);

          // Integrantes
          const ints = relevo.integrantes || [];
          for (let k = 0; k < maxIntegrantes; k++) {
            row.push(ints[k] || '');
          }
          // Suplentes
          const sups = relevo.suplentes || [];
          for (let k = 0; k < maxSuplentes; k++) {
            row.push(sups[k] || '');
          }
        } else {
          // No hay relevo => rellenar vacíos
          row.push('');
          row.push('');
          row.push('');
          for (let k = 0; k < maxIntegrantes; k++) row.push('');
          for (let k = 0; k < maxSuplentes; k++) row.push('');
        }
      }
    }

    csvData.push(row);
  });

  // 4) Generar CSV con papaparse
  let csv = Papa.unparse(csvData, {
    quotes: false, // no forzamos con papaparse
    header: false, // primera fila = header
    delimiter,
  });

  // BOM
  const csvWithBOM = '\ufeff' + csv;

  // 5) Descargar
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${competitionName}_inscritos_personalizado.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
